import { useEffect, useRef, useState } from "react";
import type { Point } from "./view";

/** A courier's new position is reached in this long: he moves, he does not jump. */
const GLIDE_MS = 1_000;

const easeInOut = (time: number) => (time < 0.5 ? 2 * time * time : 1 - (-2 * time + 2) ** 2 / 2);

/**
 * Where each mark is drawn, on the map itself (Lambert 72): a new position glides there from where the
 * mark is now. The map can be dragged and zoomed meanwhile; the mark stays on its place on the map, it
 * does not trail behind it (Furkan, 10/10/2026: "bij verschuiven van de kaart schuift de locatie van de
 * koerier mee, pas na 1 sec komt die terug").
 */
export function useGlide(targets: ReadonlyMap<string, Point>): ReadonlyMap<string, Point> {
  const [shown, setShown] = useState<ReadonlyMap<string, Point>>(targets);
  const current = useRef<ReadonlyMap<string, Point>>(targets);

  useEffect(() => {
    const from = current.current;
    const moving = [...targets].some(([id, point]) => {
      const was = from.get(id);
      return was !== undefined && (was.x !== point.x || was.y !== point.y);
    });
    const still =
      typeof requestAnimationFrame === "undefined" ||
      (typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
    if (!moving || still) {
      current.current = targets;
      setShown(targets);
      return;
    }
    let start: number | null = null;
    let frame = 0;
    const step = (time: number) => {
      start ??= time;
      const progress = Math.min(1, Math.max(0, (time - start) / GLIDE_MS));
      const eased = easeInOut(progress);
      const now = new Map<string, Point>();
      for (const [id, point] of targets) {
        const was = from.get(id) ?? point;
        now.set(id, { x: was.x + (point.x - was.x) * eased, y: was.y + (point.y - was.y) * eased });
      }
      current.current = now;
      setShown(now);
      if (progress < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => {
      cancelAnimationFrame(frame);
    };
  }, [targets]);

  // a courier who is new on the map, or gone, is so at once
  const visible = new Map<string, Point>();
  for (const [id, point] of targets) visible.set(id, shown.get(id) ?? point);
  return visible;
}
